import React, { useEffect, useRef, useState } from "react";
import {
	Grid,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	useMediaQuery,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { Box } from "@mui/system";
import TextFieldController from "../inputs/TextFieldController";
import DropdownController from "../inputs/DropdownController";
import DatePickerController from "../inputs/DatePickerController";
import CheckboxController from "../inputs/CheckboxController";
import RadioController from "../inputs/RadioController";
import FreeTextDropdownController from "../inputs/FreeTextDropdownController";

const FormSection = ({
	title,
	icon,
	fields,
	control,
	errors,
	options,
	theme,
	isExpandFunctionality = true,
	allExpanded = false,
	defaultExpanded = false,
	renderExtraContent,
	watch,
	handleIconClick,
	renderCardContent,
	id,
	editPermission,
	renderIdGenerationButton,
	isEdit,
}) => {
	const [expanded, setExpanded] = useState(defaultExpanded || allExpanded);

	const isInitialMount = useRef(true);
	const isTablet = useMediaQuery(theme.breakpoints.down("md"));

	// Sync with allExpanded, but skip on initial mount to respect defaultExpanded
	useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}
		setExpanded(allExpanded);
	}, [allExpanded]);

	const renderFields = (
		<Grid container spacing={2}>
			{fields.map((field) => {
				if (field.isBreak) {
					return <Grid item xs={12} sm={6} md={3} key={field.name} />;
				}

				if (field.isPhoto && id && renderCardContent) {
					return (
						<Grid
							item
							xs={12}
							sm={12}
							md={3}
							key={field.name}
							display="flex"
							justifyContent={isTablet ? "center" : "end"}
							alignItems="center"
						>
							{id && renderCardContent()}
						</Grid>
					);
				}

				return (
					<React.Fragment key={field.name}>
						{renderExtraContent && renderExtraContent(field)}
						<Grid item xs={12} sm={6} md={field.md || 3}>
							{field.component === "TextField" && (
								<Box display="flex" alignItems="center">
									<TextFieldController
										name={field.name}
										label={field.label}
										type={field.type}
										required={field.required}
										control={control}
										errors={errors}
										isPhoneNumber={field.isPhoneNumber}
										isSSN={field.isSSN}
										isNumber={field.isNumber}
										disabled={
											!editPermission ||
											(typeof field.isDisabled ===
											"function"
												? field.isDisabled(watch())
												: field.isDisabled)
										}
									/>
									{field.isIdField &&
										!isEdit &&
										renderIdGenerationButton()}
								</Box>
							)}

							{field.component === "Dropdown" && (
								<DropdownController
									name={field.name}
									label={field.label}
									options={options[field.name] || []}
									control={control}
									errors={errors}
									disabled={
										!editPermission ||
										(typeof field.isDisabled === "function"
											? field.isDisabled(watch())
											: field.isDisabled)
									}
									startIcon={field.startIcon}
									handleIconClick={handleIconClick}
								/>
							)}
							{field.component === "DatePicker" && (
								<DatePickerController
									name={field.name}
									label={field.label}
									control={control}
									errors={errors}
									disableFuture={field.disableFuture}
									disabled={
										!editPermission ||
										(typeof field.isDisabled === "function"
											? field.isDisabled(watch())
											: field.isDisabled)
									}
								/>
							)}
							{field.component === "Checkbox" && (
								<CheckboxController
									name={field.name}
									label={field.label}
									required={field.required}
									control={control}
									errors={errors}
									disabled={
										!editPermission ||
										(typeof field.isDisabled === "function"
											? field.isDisabled(watch())
											: field.isDisabled)
									}
								/>
							)}
							{field.component === "Radio" && (
								<RadioController
									name={field.name}
									label={field.label}
									options={options[field.name] || []}
									required={field.required}
									control={control}
									errors={errors}
									disabled={
										!editPermission ||
										(typeof field.isDisabled === "function"
											? field.isDisabled(watch())
											: field.isDisabled)
									}
								/>
							)}
							{field.component === "FreeTextDropdown" && (
								<FreeTextDropdownController
									name={field.name}
									label={field.label}
									options={options[field.name] || []}
									control={control}
									errors={errors}
									disabled={
										!editPermission ||
										(typeof field.isDisabled === "function"
											? field.isDisabled(watch())
											: field.isDisabled)
									}
								/>
							)}
						</Grid>
					</React.Fragment>
				);
			})}
		</Grid>
	);

	return isExpandFunctionality ? (
		<Accordion
			expanded={expanded}
			onChange={() => setExpanded((prev) => !prev)}
			sx={{ mb: 1 }}
		>
			<AccordionSummary
				expandIcon={<ExpandMore />}
				sx={{
					backgroundColor: theme.accordion.backgroundColor,
					color: theme.accordion.textColor,
				}}
			>
				<Box display="flex" alignItems="center">
					{icon && <Box sx={{ mr: 1 }}>{icon}</Box>}
					{title}
				</Box>
			</AccordionSummary>
			<AccordionDetails sx={{ marginTop: theme.spacing(1) }}>
				{renderFields}
			</AccordionDetails>
		</Accordion>
	) : (
		renderFields
	);
};

export default FormSection;
